function getRuntimeGlobal() {
    if (typeof window !== "undefined") {
        return window;
    }
    return globalThis;
}
function positiveInt(value, fallback) {
    if (!Number.isFinite(value) || value <= 0) {
        return fallback;
    }
    return Math.floor(value);
}
export class FFIBenchmark {
    constructor() {
        this._commandBuffer = new ArrayBuffer(0);
        this._commandView = new Float64Array(this._commandBuffer);
        const runtimeGlobal = getRuntimeGlobal();
        this._generic = this.requireFunction(runtimeGlobal, "conchFFIBenchmarkGeneric");
        this._raw = this.requireFunction(runtimeGlobal, "conchFFIBenchmarkRaw");
        this._fast = this.requireFunction(runtimeGlobal, "conchFFIBenchmarkFast");
        this._commandBufferNative = this.requireFunction(runtimeGlobal, "conchFFIBenchmarkCommandBuffer");
        this._cppDirect = this.requireFunction(runtimeGlobal, "conchFFIBenchmarkCppDirect");
        this._reset = this.requireFunction(runtimeGlobal, "conchFFIBenchmarkReset");
        this._getSink = this.requireFunction(runtimeGlobal, "conchFFIBenchmarkGetSink");
        this._getCallCount = this.requireFunction(runtimeGlobal, "conchFFIBenchmarkGetCallCount");
        this._isFastApiEnabled = this.requireFunction(runtimeGlobal, "conchFFIBenchmarkIsFastApiEnabled");
    }
    static run(options = {}) {
        return new FFIBenchmark().run(options);
    }
    run(options = {}) {
        const resolved = this.resolveOptions(options);
        const results = [];
        results.push(this.measure("cppDirect", resolved, (calls, inner, seed) => {
            this.runCppDirect(calls, inner, seed);
        }, 1));
        results.push(this.measure("generic", resolved, (calls, inner, seed) => {
            this.runGeneric(calls, inner, seed);
        }, resolved.calls));
        results.push(this.measure("raw", resolved, (calls, inner, seed) => {
            this.runRaw(calls, inner, seed);
        }, resolved.calls));
        results.push(this.measure("fast", resolved, (calls, inner, seed) => {
            this.runFast(calls, inner, seed);
        }, resolved.calls));
        results.push(this.measure("commandBuffer", resolved, (calls, inner, seed) => {
            this.runCommandBuffer(calls, inner, seed, resolved.commandBatchSize);
        }, Math.ceil(resolved.calls / resolved.commandBatchSize)));
        this.logSummary(results, resolved);
        return results;
    }
    resolveOptions(options) {
        const calls = positiveInt(options.calls, 200000);
        const warmupCalls = positiveInt(options.warmupCalls, 20000);
        const rounds = positiveInt(options.rounds, 5);
        const innerIterations = positiveInt(options.innerIterations, 8);
        const seed = Number.isFinite(options.seed) ? Math.floor(options.seed) : 305419896;
        let commandBatchSize = positiveInt(options.commandBatchSize, 4096);
        if (commandBatchSize > calls) {
            commandBatchSize = calls;
        }
        return {
            calls,
            warmupCalls,
            rounds,
            innerIterations,
            seed,
            commandBatchSize,
            printJsonReport: options.printJsonReport !== false
        };
    }
    measure(mode, options, runner, estimatedFfiCalls) {
        this._reset();
        runner(options.warmupCalls, options.innerIterations, options.seed ^ 0x5f3759df);
        let minMs = Number.POSITIVE_INFINITY;
        let totalMs = 0;
        let sink = 0;
        let nativeLogicalCalls = 0;
        for (let round = 0; round < options.rounds; round++) {
            this._reset();
            const start = this.now();
            runner(options.calls, options.innerIterations, options.seed + round * 1000003);
            const elapsed = this.now() - start;
            if (elapsed < minMs) {
                minMs = elapsed;
            }
            totalMs += elapsed;
            sink = this._getSink();
            nativeLogicalCalls = this._getCallCount();
        }
        const avgMs = totalMs / options.rounds;
        return {
            mode,
            calls: options.calls,
            innerIterations: options.innerIterations,
            rounds: options.rounds,
            minMs,
            avgMs,
            minNsPerCall: minMs * 1000000 / options.calls,
            avgNsPerCall: avgMs * 1000000 / options.calls,
            estimatedFfiCalls,
            nativeLogicalCalls,
            sink,
            fastApiEnabled: this._isFastApiEnabled()
        };
    }
    runGeneric(calls, innerIterations, seedBase) {
        const generic = this._generic;
        for (let i = 0; i < calls; i++) {
            generic(innerIterations, seedBase + i, 1.0 + (i & 15) * 0.03125, -0.25 + (i & 7) * 0.0625);
        }
    }
    runRaw(calls, innerIterations, seedBase) {
        const raw = this._raw;
        for (let i = 0; i < calls; i++) {
            raw(innerIterations, seedBase + i, 1.0 + (i & 15) * 0.03125, -0.25 + (i & 7) * 0.0625);
        }
    }
    runFast(calls, innerIterations, seedBase) {
        const fast = this._fast;
        for (let i = 0; i < calls; i++) {
            fast(innerIterations, seedBase + i, 1.0 + (i & 15) * 0.03125, -0.25 + (i & 7) * 0.0625);
        }
    }
    runCppDirect(calls, innerIterations, seedBase) {
        this._cppDirect(calls, innerIterations, seedBase);
    }
    runCommandBuffer(calls, innerIterations, seedBase, batchSize) {
        const commandBufferNative = this._commandBufferNative;
        let submitted = 0;
        while (submitted < calls) {
            const commandCount = Math.min(batchSize, calls - submitted);
            const commands = this.ensureCommandCapacity(commandCount);
            for (let i = 0; i < commandCount; i++) {
                const logicalIndex = submitted + i;
                const offset = i * 4;
                commands[offset] = innerIterations;
                commands[offset + 1] = seedBase + logicalIndex;
                commands[offset + 2] = 1.0 + (logicalIndex & 15) * 0.03125;
                commands[offset + 3] = -0.25 + (logicalIndex & 7) * 0.0625;
            }
            commandBufferNative(this._commandBuffer, commandCount);
            submitted += commandCount;
        }
    }
    ensureCommandCapacity(commandCount) {
        const requiredLength = commandCount * 4;
        if (this._commandView.length < requiredLength) {
            this._commandBuffer = new ArrayBuffer(requiredLength * 8);
            this._commandView = new Float64Array(this._commandBuffer);
        }
        return this._commandView;
    }
    requireFunction(runtimeGlobal, name) {
        const value = runtimeGlobal[name];
        if (typeof value !== "function") {
            throw new Error(`[FFIBenchmark] window.${name} is not available.`);
        }
        return value;
    }
    logSummary(results, options) {
        const fastApiEnabled = results.length > 0 ? results[0].fastApiEnabled : false;
        console.log(`[FFIBenchmark] calls=${options.calls}, warmup=${options.warmupCalls}, rounds=${options.rounds}, ` +
            `innerIterations=${options.innerIterations}, commandBatchSize=${options.commandBatchSize}, ` +
            `fastApiEnabled=${fastApiEnabled}`);
        for (const result of results) {
            console.log(`[FFIBenchmark] ${result.mode.padEnd(14)} ` +
                `min=${result.minMs.toFixed(3)}ms avg=${result.avgMs.toFixed(3)}ms ` +
                `min=${result.minNsPerCall.toFixed(1)}ns/call avg=${result.avgNsPerCall.toFixed(1)}ns/call ` +
                `estFfiCalls=${result.estimatedFfiCalls} nativeCalls=${result.nativeLogicalCalls} ` +
                `sink=${result.sink.toFixed(3)}`);
        }
        if (options.printJsonReport) {
            const report = this.createReport(results, options, fastApiEnabled);
            console.log("[FFIBenchmark:JSON_BEGIN]");
            console.log(JSON.stringify(report, null, 2));
            console.log("[FFIBenchmark:JSON_END]");
        }
    }
    createReport(results, options, fastApiEnabled) {
        const runtimeGlobal = getRuntimeGlobal();
        const navigatorLike = runtimeGlobal.navigator;
        return {
            name: "JSBind FFI Benchmark",
            timestamp: new Date().toISOString(),
            runtime: {
                userAgent: navigatorLike && navigatorLike.userAgent ? navigatorLike.userAgent : "",
                platform: navigatorLike && navigatorLike.platform ? navigatorLike.platform : "",
                fastApiEnabled
            },
            options,
            results
        };
    }
    now() {
        if (typeof performance !== "undefined" && performance.now) {
            return performance.now();
        }
        return Date.now();
    }
}
